import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Settings2,
  Layers,
  DollarSign,
  GitBranch,
  LogOut,
  Tag,
  Palette,
  Crown,
  Shield,
  Menu,
  X,
  Mail,
  MessageCircle,
  Link2,
  Languages,
  Gift,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { useAuth } from '../context/AuthContext';
import { ReviewPromptToast } from './ReviewPromptToast';
import { OnboardingTour } from './OnboardingTour';
import { usePlan } from '../lib/usePlan';

const ADMIN_EMAIL = 'covered.ro@gmail.com';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/products', icon: Package, label: 'Products' },
  { path: '/option-sets', icon: Layers, label: 'Option Sets' },
  { path: '/pricing-rules', icon: DollarSign, label: 'Pricing Rules' },
  { path: '/logic', icon: GitBranch, label: 'Conditional Logic' },
  { path: '/variant-mapping', icon: Tag, label: 'Variant Mapping' },
  { path: '/bundles', icon: Gift, label: 'Bundles' },
  { path: '/bridge-products', icon: Link2, label: 'Bridge Products' },
  { path: '/translations', icon: Languages, label: 'Translations' },
  { path: '/widget-customizer', icon: Palette, label: 'Widget Style' },
  { path: '/billing', icon: Crown, label: 'Subscription' },
  { path: '/settings', icon: Settings2, label: 'Settings' },
];

export const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isPremium } = usePlan();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        {/* Mobile header */}
        <header className="mobile-header" data-testid="mobile-header">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Logo" className="w-7 h-7 rounded-md" />
            <span className="font-semibold text-base">Advanced Options</span>
          </Link>
          <div className="w-9" />
        </header>

        {/* Mobile overlay */}
        {mobileOpen && (
          <div
            className="mobile-overlay"
            data-testid="mobile-overlay"
            onClick={() => setMobileOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside className={`sidebar ${mobileOpen ? 'sidebar-open' : ''}`} data-testid="sidebar">
          <div className="p-6 flex-1 overflow-y-auto min-h-0">
            {/* Logo + close button on mobile */}
            <div className="flex items-center justify-between mb-10">
              <Link to="/" className="flex items-center gap-3">
                <img src="/logo.png" alt="Logo" className="w-9 h-9 rounded-lg" />
                <span className="font-semibold text-lg nav-text">Advanced Options</span>
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileOpen(false)}
                className="mobile-close-btn"
                data-testid="mobile-close-btn"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Navigation */}
            <nav className="space-y-1 stagger-children">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const isHighlighted = item.path === '/option-sets' || item.path === '/billing';
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>
                      <Link
                        to={item.path}
                        data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                        className={`nav-item ${isActive ? 'active' : ''} ${isHighlighted ? 'nav-highlight' : ''}`}
                      >
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        <span className="nav-text">{item.label}</span>
                        {item.path === '/logic' && !isPremium && (
                          <Crown className="w-3.5 h-3.5 text-yellow-500 ml-auto flex-shrink-0" />
                        )}
                        {item.path === '/pricing-rules' && !isPremium && (
                          <Crown className="w-3.5 h-3.5 text-yellow-500 ml-auto flex-shrink-0" />
                        )}
                        {item.path === '/variant-mapping' && !isPremium && (
                          <Crown className="w-3.5 h-3.5 text-yellow-500 ml-auto flex-shrink-0" />
                        )}
                        {item.path === '/bundles' && !isPremium && (
                          <Crown className="w-3.5 h-3.5 text-yellow-500 ml-auto flex-shrink-0" />
                        )}
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="md:hidden">
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </nav>

            {/* Admin link - only for admin */}
            {user?.email === ADMIN_EMAIL && (
              <div className="mt-4 pt-4 border-t border-border">
                <Link
                  to="/admin"
                  data-testid="nav-admin"
                  className={`nav-item ${location.pathname === '/admin' ? 'active' : ''}`}
                >
                  <Shield className="w-5 h-5 flex-shrink-0 text-destructive" />
                  <span className="nav-text">Admin</span>
                </Link>
              </div>
            )}

            {/* Contact section */}
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 nav-text">Contact</p>
              <a
                href="mailto:xpapion@gmail.com?subject=Advanced Product Options - Support"
                className="nav-item"
                data-testid="nav-contact-email"
              >
                <Mail className="w-5 h-5 flex-shrink-0" />
                <span className="nav-text">Email Support</span>
              </a>
              <a
                href="https://wa.me/40750422122"
                target="_blank"
                rel="noopener noreferrer"
                className="nav-item"
                data-testid="nav-contact-whatsapp"
              >
                <MessageCircle className="w-5 h-5 flex-shrink-0" />
                <span className="nav-text">WhatsApp</span>
              </a>
            </div>
          </div>

          {/* User section */}
          <div className="p-4 border-t border-border flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-medium">
                    {user?.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0 nav-text">
                  <p className="text-sm font-medium truncate">{user?.email}</p>
                  {user?.shop_domain && (
                    <p className="text-xs text-muted-foreground truncate">
                      {user.shop_domain}
                    </p>
                  )}
                </div>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleLogout}
                    data-testid="logout-btn"
                    className="flex-shrink-0"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Logout</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="main-content">
          {children}
          <footer className="px-6 md:px-10 py-4 mt-8 border-t border-border">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>&copy; {new Date().getFullYear()} Advanced Product Options</span>
              <Link to="/privacy-policy" className="hover:text-foreground transition-colors" data-testid="footer-privacy-link">
                Privacy Policy
              </Link>
            </div>
          </footer>
        </main>
      </div>
      <ReviewPromptToast user={user} />
      <OnboardingTour user={user} />
    </TooltipProvider>
  );
};
